import type { Reading } from "@spf/contracts";

export interface CounterFigure {
  label: string;
  value: number | null;
}

/**
 * The inverter's four energy counters, named for what they were measured to
 * count rather than for what their registers are called.
 *
 * Slice 4b labelled `eac_dischar` "delivered to the house" and `eac_char` "into
 * the battery". Both were true only while the grid supplied everything. Once a
 * PV array was connected on 2026-09-27 the first read 0.0 kWh on a day the
 * house used 6.8, and the second 0.1 kWh on a day the battery took in 11.9:
 * one counts load served from the grid, the other charging from AC.
 * `ebat_dischar` matched the integrated discharge on every day either side.
 * docs/DECISIONS/0027 has the day-by-day figures.
 *
 * `epv_total` and `epv_today` count what the panels generated: the total rose
 * 13.6 kWh across 2026-09-28 against 13.45 integrated from the panels' power,
 * and the daily one read 9.8 on 2026-09-29 against 9.73 (docs/DECISIONS/0028).
 * It is last, so the screens that take the first two -- the battery's -- still
 * do.
 *
 * One list, so every screen that shows a counter calls it the same thing.
 */
export function counters(reading: Reading, span: "today" | "total"): CounterFigure[] {
  const today = span === "today";
  return [
    {
      label: "Charged from the grid",
      value: today ? reading.energyChargedTodayKwh : reading.energyChargedTotalKwh,
    },
    {
      label: "Discharged from the battery",
      value: today ? reading.batteryDischargedTodayKwh : reading.batteryDischargedTotalKwh,
    },
    {
      label: "House load from the grid",
      value: today ? reading.acOutputTodayKwh : reading.acOutputTotalKwh,
    },
    {
      label: "Generated by the panels",
      value: today ? reading.pvEnergyTodayKwh : reading.pvEnergyTotalKwh,
    },
  ];
}
